/**
 * The RAF game loop: eye pipeline -> applyCalibration -> OneEuro2D ->
 * steerFromGaze -> blendAssist (Jev steering channel, EYES mode, `?steer=0` to
 * disable) -> applyVelocity -> stepGame (eye mode), or a direct pointer -> board
 * mapping (mouse mode) -> stepGame. Reacts to `stepGame`'s events with the side
 * effects the task calls for: 'scare' awaits `ScareScene.play()` then resets to
 * 'ready' of the same level, parking the cursor; 'restart' does a brief red flash
 * (unless `reducedMotion`) plus a 700ms "Again" card, then folds in a drift
 * correction observation (`applyDrift`); 'goal' (not 'win') parks the cursor on the
 * next level's start, shows a 1500ms "Level N" card, and also folds in a drift
 * observation, during which input is ignored and `stepGame` isn't fed a cursor; 'win'
 * shows a Play-again screen. EYES mode also runs an eye calibration overlay
 * (`./calibration`) right after Start, and again on hotkey C (which also resets any
 * accumulated drift correction), pausing the game the same way a level/again card
 * does; hotkey S previews the scare via `ScareScene.play()` without touching game
 * state. Everything DOM/RAF here is intentionally untested — the pure per-event logic
 * it calls into (gaze sign, matrix decomposition, board mapping, calibration phase
 * timing, park position, the steer/drift math itself in `src/input/assist.ts` and
 * `src/input/drift.ts`) lives in already-tested sibling modules, plus `parkCursorFor`
 * below.
 */

import type {
  DriftState,
  FocusJudgment,
  GameEvent,
  GameState,
  GazeCalibration,
  Level,
  Rect,
  Vec2,
} from "../src";
import {
  ASSIST_DEFAULTS,
  applyCalibration,
  applyDriftObservation,
  applyVelocity,
  BOARD,
  blendAssist,
  CURSOR_RADIUS,
  createGame,
  DEFAULT_CALIBRATION,
  directionToGoal,
  distanceToWall,
  FOCUS_THRESHOLD_DEFAULT,
  LEVELS,
  OneEuro2D,
  openDirections,
  resetHunt,
  signedCentrelineOffset,
  steerFromGaze,
  stepGame,
  TelemetryWindow,
} from "../src";
import { CalibrationOverlay } from "./calibration";
import { type EyeSampleLike, getLastGazePath, toEyeSample } from "./face/gaze";
import { createLandmarker, type LandmarkerHandle } from "./face/landmarker";
import { FocusClient } from "./focus/client";
import { pointerToBoard } from "./input/mouse";
import type { ScareMazeParams } from "./params";
import {
  drawFrame,
  type HudDrift,
  type HudGaze,
  type HudInfo,
  type HudSteer,
  type HudTelemetry,
} from "./render/canvas";
import { ScareScene } from "./scare/scene";
import {
  STEER_FAST_POLL_INTERVAL_MS_DEFAULT,
  STEER_SLOW_POLL_INTERVAL_MS,
  SteerClient,
  type SteerState,
  steerPollIntervalMs,
} from "./steer/client";

export interface GameLoopHandle {
  /**
   * Tears down everything `startGameLoop` started: cancels the RAF loop
   * (which is also what drives `FocusClient.poll` — there's no separate
   * polling timer to clear), closes the `FaceLandmarker`, stops every
   * `MediaStream` track, removes the mouse-fallback/keydown listeners, and
   * disposes the calibration overlay. Safe to call more than once.
   */
  stop(): void;
}

export interface GameLoopOptions {
  readonly root: HTMLElement;
  readonly liveRegion: HTMLElement;
  readonly reducedMotion: boolean;
  readonly audio: AudioContext | null;
  readonly stream: MediaStream | null;
  readonly cameraBanner: string | null;
  /** `?audioUrl=` override for `ScareScene` — a custom scream clip instead of the synthesized one. */
  readonly audioUrl?: string;
  /** The level-3 payoff scare video (see `./main`'s `SCARE_VIDEO_URL` and
   * `public/scare/ASSETS.md`) — passed straight through to `ScareScene`. `undefined`
   * behaves exactly like the video never having been configured (built-in scare only). */
  readonly videoUrl?: string;
  /** Resolved (already-defaulted) art base URL — see `./render/art`'s
   * `resolveArtBaseUrl`, called once by `./main` before `startGameLoop`. Always a
   * usable URL ending in "/" (same-origin `/art/` when nothing else was configured),
   * never `undefined` — there's always SOME base to attempt, even though every asset
   * under it is optional (missing/failed keeps today's coded look, see `./render/canvas`). */
  readonly artBaseUrl: string;
  /** Every other `?param=` this package reads — see `./params`. */
  readonly params: ScareMazeParams;
}

// Per the cross-slice contract: beta is fixed, minCutoff defaults to 1.0 (overridable via ?minCutoff=).
const ONE_EURO_BETA = 0.02;
const ONE_EURO_MIN_CUTOFF_DEFAULT = 1.0;
const RESTART_FLASH_MS = 350;
const LEVEL_CARD_MS = 1500;
const AGAIN_CARD_MS = 700;
const MAX_DT_S = 0.1;
/** Mirrors src/focus/gate.ts's `isFocused` default `maxAgeMs` (not exported) — used
 * only to label the HUD's disarmed reason ("stale" vs "below threshold"); gameplay
 * gating itself always goes through `isFocused`/`stepGame`, never this constant. */
const FOCUS_MAX_AGE_MS = 5000;

function rectCenter(r: Rect): Vec2 {
  return { x: r.x + r.w / 2, y: r.y + r.h / 2 };
}

/** Clamps (and finite-guards) a SteerState numeric field to the `/api/steer` wire
 * contract's declared range so a malformed reading never trips the server's 400. */
function clampRange(v: number, lo: number, hi: number): number {
  if (!Number.isFinite(v)) return 0;
  return Math.max(lo, Math.min(hi, v));
}

/** Dominant-axis direction of this frame's cursor travel — used as a (necessarily
 * approximate) proxy for "which side of the corridor the cursor just hit" when a
 * 'wall' event fires, since stepGame's own sweep test doesn't expose which wall it
 * was. `{0,0}` (no motion) reads as "none". */
function wallSideFromTravel(dx: number, dy: number): "left" | "right" | "up" | "down" | "none" {
  if (dx === 0 && dy === 0) return "none";
  if (Math.abs(dx) >= Math.abs(dy)) return dx > 0 ? "right" : "left";
  return dy > 0 ? "down" : "up";
}

/** Pure: the parked cursor position for `levelIndex`'s start pad centre — used to
 * reset `position`/`prevPosition` on a level change, a silent restart, or once a
 * scare resolves, so the player always re-enters a level centred on its start pad
 * rather than wherever the previous level's cursor happened to be. */
export function parkCursorFor(levelIndex: number): Vec2 {
  const level = LEVELS[levelIndex] as Level;
  return rectCenter(level.start);
}

/** Mirrors `game/state.ts`'s internal `enterLevel` using only exported primitives (LEVELS, resetHunt) — stepGame itself never re-exposes a mid-scare "reset to ready" transition, since 'scare' is terminal from its perspective by design. */
function resetToReady(state: GameState, now: number): GameState {
  const level = LEVELS[state.levelIndex] as Level;
  return {
    levelIndex: state.levelIndex,
    phase: "ready",
    corridors: level.corridors.map((r) => ({ ...r })),
    hunt: resetHunt(level),
    started: true,
    levelStartedAt: now,
    fails: state.fails,
    // Matches `enterLevel`'s own reset-on-entry value — null outside
    // 'playing'/immediately after a restart, per GameState's doc comment.
    prevCursor: null,
  };
}

function setLiveBanner(liveRegion: HTMLElement, text: string | null): void {
  if (liveRegion.textContent === (text ?? "")) return;
  liveRegion.textContent = text ?? "";
  liveRegion.classList.toggle("visible", text !== null);
}

/** `judgment === null` reads as "unconfigured" — this HUD label doesn't distinguish
 * a never-configured TypeSafe key from simply not having received a first judgment
 * yet; both mean "no signal to arm on" from the player's point of view. */
function computeFocusStatus(
  judgment: FocusJudgment | null,
  nowMs: number,
  threshold: number,
): HudInfo["focusStatus"] {
  if (judgment === null) return "disarmed: unconfigured";
  if (nowMs - judgment.t > FOCUS_MAX_AGE_MS) return "disarmed: stale";
  if (judgment.p < threshold) return "disarmed: below threshold";
  return "ARMED";
}

export function startGameLoop(opts: GameLoopOptions): GameLoopHandle {
  let mode: "EYES" | "MOUSE" = opts.stream !== null ? "EYES" : "MOUSE";
  const params = opts.params;

  function stopMediaStreamTracks(): void {
    if (opts.stream === null) return;
    for (const track of opts.stream.getTracks()) track.stop();
  }

  const boardWrap = document.createElement("div");
  boardWrap.className = "board-wrap";
  const canvas = document.createElement("canvas");
  canvas.className = "board-canvas";
  canvas.width = BOARD.w;
  canvas.height = BOARD.h;
  const flash = document.createElement("div");
  flash.className = "board-flash";
  boardWrap.appendChild(canvas);
  boardWrap.appendChild(flash);
  opts.root.appendChild(boardWrap);

  const maybeCtx = canvas.getContext("2d");
  if (maybeCtx === null) {
    setLiveBanner(
      opts.liveRegion,
      "This browser has no 2D canvas support — the game can't render.",
    );
    // Nothing will ever run to stop these otherwise — no RAF loop starts.
    stopMediaStreamTracks();
    return { stop(): void {} };
  }
  // Re-bound to a fresh, non-null-typed const: TS doesn't carry the null
  // check above into the `frame` closure defined further down.
  const ctx: CanvasRenderingContext2D = maybeCtx;

  let video: HTMLVideoElement | null = null;
  let landmarker: LandmarkerHandle | null = null;
  let eyePipelineBanner: string | null = null;

  const scareScene = new ScareScene(opts.root, {
    reducedMotion: opts.reducedMotion,
    audio: opts.audio,
    audioUrl: opts.audioUrl,
    videoUrl: opts.videoUrl,
  });
  const oneEuro = new OneEuro2D({
    minCutoff: params.minCutoff ?? ONE_EURO_MIN_CUTOFF_DEFAULT,
    beta: ONE_EURO_BETA,
  });
  const telemetryWindow = new TelemetryWindow();
  const focusClient = new FocusClient();
  const steerClient = new SteerClient({ fastPollIntervalMs: params.steerPoll });
  const calibrationOverlay = new CalibrationOverlay(opts.root);

  let gameState: GameState = createGame();
  let position: Vec2 | null = mode === "MOUSE" ? null : parkCursorFor(0);
  let prevPosition: Vec2 = position ?? parkCursorFor(0);
  let lastFrameMs: number | null = null;
  let fps = 0;
  let flashTimer: ReturnType<typeof setTimeout> | null = null;
  let cardTimer: ReturnType<typeof setTimeout> | null = null;
  let winScreen: HTMLElement | null = null;
  let scareInFlight = false;
  let lastTelemetry: HudTelemetry | null = null;
  let lastGazeHud: HudGaze | null = null;
  let lastFilteredGaze: Vec2 | null = null;
  let lastSteerHud: HudSteer | null = null;
  let lastDriftHud: HudDrift = { offsetX: 0, offsetY: 0, pollMs: STEER_SLOW_POLL_INTERVAL_MS };
  let driftState: DriftState = { offsetX: 0, offsetY: 0 };
  let lastWallSide: "left" | "right" | "up" | "down" | "none" = "none";
  let calibration: GazeCalibration = DEFAULT_CALIBRATION;
  let calibrationWeakBanner: string | null = null;
  // EYES mode calibrates before play starts; MOUSE mode never calibrates.
  let uiOverlay: "none" | "calibrating" | "levelCard" | "againCard" =
    mode === "EYES" ? "calibrating" : "none";

  let mouseListenersInstalled = false;

  function handlePointerMove(ev: PointerEvent): void {
    if (uiOverlay !== "none") return; // gaze/mouse input is ignored during any overlay
    const rect = canvas.getBoundingClientRect();
    position = pointerToBoard(ev.clientX, ev.clientY, rect);
  }

  /** Idempotent — safe to call from both the initial MOUSE-mode setup and the EYES-mode landmarker-failure fallback below. */
  function installMouseListeners(): void {
    if (mouseListenersInstalled) return;
    mouseListenersInstalled = true;
    canvas.addEventListener("pointermove", handlePointerMove);
  }

  function removeMouseListeners(): void {
    if (!mouseListenersInstalled) return;
    mouseListenersInstalled = false;
    canvas.removeEventListener("pointermove", handlePointerMove);
  }

  /** Camera was granted but the landmarker failed to initialise: the stream would otherwise stay live with nothing consuming it and the player would have no way to steer. Drop to mouse mode instead of just changing the banner. */
  function fallBackToMouse(banner: string): void {
    stopMediaStreamTracks();
    mode = "MOUSE";
    eyePipelineBanner = banner;
    // No camera means calibration can never start — don't leave the player stuck
    // waiting on an overlay that can never resolve.
    uiOverlay = "none";
    calibrationOverlay.dispose();
    installMouseListeners();
  }

  if (mode === "EYES" && opts.stream !== null) {
    video = document.createElement("video");
    video.className = "board-video-hidden";
    video.autoplay = true;
    video.muted = true;
    video.playsInline = true;
    video.srcObject = opts.stream;
    boardWrap.appendChild(video);
    void video.play().catch(() => {
      // Autoplay can still be blocked in some embeddings — detection just
      // won't start until playback does; no fallback needed beyond that.
    });

    createLandmarker(params.delegate === "gpu" ? "GPU" : "CPU")
      .then((handle) => {
        landmarker = handle;
      })
      .catch(() => {
        fallBackToMouse("Face tracking failed to load — steer with the mouse instead.");
      });
  }

  if (mode === "MOUSE") {
    installMouseListeners();
  }

  function triggerRestartFlash(): void {
    if (opts.reducedMotion) return;
    flash.classList.remove("active");
    // Force a reflow so re-adding the class retriggers the CSS transition
    // even if a previous flash is still fading.
    void flash.offsetWidth;
    flash.classList.add("active");
    if (flashTimer !== null) clearTimeout(flashTimer);
    flashTimer = setTimeout(() => flash.classList.remove("active"), RESTART_FLASH_MS);
  }

  function parkCursor(levelIndex: number): void {
    const parked = parkCursorFor(levelIndex);
    position = parked;
    prevPosition = parked;
  }

  /** Transient, non-interactive "Level N" / "Again" card — an aria-live-announced
   * text overlay, not a modal (no focus trap, nothing to click). `uiOverlay` gates
   * cursor movement and stepGame's cursor input for its `ms` duration, then reverts
   * to "none" (unless something else already changed it in the meantime). */
  function showCard(text: string, ms: number, kind: "levelCard" | "againCard"): void {
    uiOverlay = kind;
    const card = document.createElement("div");
    card.setAttribute("role", "status");
    card.setAttribute("aria-live", "assertive");
    card.textContent = text;
    card.style.position = "fixed";
    card.style.inset = "0";
    card.style.zIndex = "1500";
    card.style.display = "flex";
    card.style.alignItems = "center";
    card.style.justifyContent = "center";
    card.style.fontSize = "40px";
    card.style.color = "#d8d8e8";
    card.style.background = "rgba(8, 8, 12, 0.55)";
    card.style.pointerEvents = "none";
    opts.root.appendChild(card);
    if (cardTimer !== null) clearTimeout(cardTimer);
    cardTimer = setTimeout(() => {
      card.remove();
      if (uiOverlay === kind) uiOverlay = "none";
      cardTimer = null;
    }, ms);
  }

  function showWinScreen(): void {
    if (winScreen !== null) return;
    const screen = document.createElement("div");
    screen.className = "win-screen";
    const heading = document.createElement("h2");
    heading.textContent = "You survived. Barely.";
    const again = document.createElement("button");
    again.type = "button";
    again.textContent = "Play again";
    again.addEventListener("click", () => {
      screen.remove();
      winScreen = null;
      gameState = createGame();
      uiOverlay = "none";
      position = mode === "MOUSE" ? null : parkCursorFor(0);
      prevPosition = position ?? parkCursorFor(0);
    });
    screen.appendChild(heading);
    screen.appendChild(again);
    opts.root.appendChild(screen);
    winScreen = screen;
  }

  /** Folds the steering channel's latest `calibrationDrift` judgment into `driftState`
   * — called only at a level change or a silent restart (never mid-corridor), per the
   * operator decision. No-ops when `?drift=0` or there's no drift observation yet. */
  function applyDrift(): void {
    if (!params.drift) return;
    const drift = steerClient.getDrift();
    if (drift === null) return;
    const judgment = steerClient.getJudgment();
    driftState = applyDriftObservation(
      driftState,
      drift.direction,
      drift.confidence,
      judgment?.reliable ?? 0,
    );
  }

  /** `useVideo` selects the level-3 payoff (`ScareScene.playVideo()`, which itself falls
   * back to the built-in scare if the video never armed) vs. the built-in scare levels 1
   * and 2 always use. Either way, the post-scare reset (restart level 3, or the current
   * level generally, and keep playing) is identical — the `finally` block below never
   * varies by scare type. */
  async function handleScare(useVideo: boolean): Promise<void> {
    if (scareInFlight) return;
    scareInFlight = true;
    try {
      await (useVideo ? scareScene.playVideo() : scareScene.play());
    } finally {
      gameState = resetToReady(gameState, performance.now());
      parkCursor(gameState.levelIndex);
      scareInFlight = false;
    }
  }

  function handleEvents(events: readonly GameEvent[], previousLevelIndex: number): void {
    const isWin = events.includes("win");
    for (const event of events) {
      if (event === "scare") {
        console.log("scarymaze: scare");
        const scaredLevel = LEVELS[previousLevelIndex] as Level;
        void handleScare(scaredLevel.id === 3);
      } else if (event === "restart") {
        triggerRestartFlash();
        parkCursor(gameState.levelIndex);
        showCard("Again", AGAIN_CARD_MS, "againCard");
        applyDrift();
      } else if (event === "goal") {
        const completedLevel = LEVELS[previousLevelIndex] as Level;
        console.log(`scarymaze: goal level ${completedLevel.id}`);
        if (!isWin) {
          parkCursor(gameState.levelIndex);
          const nextLevel = LEVELS[gameState.levelIndex] as Level;
          showCard(`Level ${nextLevel.id}`, LEVEL_CARD_MS, "levelCard");
          applyDrift();
        }
      } else if (event === "win") {
        console.log("scarymaze: won");
        showWinScreen();
      }
    }
  }

  function handleKeydown(ev: KeyboardEvent): void {
    const key = ev.key.toLowerCase();
    if (key === "c") {
      if (mode !== "EYES" || uiOverlay !== "none") return;
      uiOverlay = "calibrating"; // picked up by the next frame's calibrating branch
      driftState = { offsetX: 0, offsetY: 0 }; // recalibrating supersedes any prior drift fix-up
    } else if (key === "s") {
      if (uiOverlay !== "none") return;
      // Preview only — game state is untouched. Mirrors whichever scare the current
      // level would actually trigger (level 3's video payoff vs. the built-in scare).
      const previewLevel = LEVELS[gameState.levelIndex] as Level;
      void (previewLevel.id === 3 ? scareScene.playVideo() : scareScene.play());
    }
  }
  window.addEventListener("keydown", handleKeydown);

  let rafId: number | null = null;

  function frame(nowMs: number): void {
    rafId = requestAnimationFrame(frame);

    const dtS =
      lastFrameMs === null ? 1 / 60 : Math.min(MAX_DT_S, Math.max(0, (nowMs - lastFrameMs) / 1000));
    lastFrameMs = nowMs;
    fps = dtS > 0 ? fps * 0.9 + (1 / dtS) * 0.1 : fps;

    let sample: EyeSampleLike | null = null;
    if (mode === "EYES" && landmarker !== null && video !== null && video.readyState >= 2) {
      const result = landmarker.detect(video, nowMs);
      if (result !== null) {
        sample = toEyeSample(result, nowMs);
        telemetryWindow.push(sample);
      }
    }

    if (uiOverlay === "calibrating") {
      if (
        !calibrationOverlay.active &&
        landmarker !== null &&
        video !== null &&
        video.readyState >= 2
      ) {
        void calibrationOverlay.run(nowMs).then((cal) => {
          calibration = cal;
          calibrationWeakBanner = cal.ok
            ? null
            : "calibration weak — using defaults, press C to retry";
          uiOverlay = "none";
        });
      }
      calibrationOverlay.feedSample(
        nowMs,
        sample !== null && sample.facePresent ? sample.gaze : null,
      );
    } else if (uiOverlay === "none" && mode === "EYES" && sample !== null && sample.facePresent) {
      if (position === null) position = parkCursorFor(gameState.levelIndex);
      // Drift correction composes on top of `calibration` rather than replacing it —
      // `calibration` itself only changes on a fresh C-run; see applyDrift/handleKeydown.
      const effectiveCalibration: GazeCalibration =
        driftState.offsetX === 0 && driftState.offsetY === 0
          ? calibration
          : {
              ...calibration,
              neutral: {
                x: calibration.neutral.x + driftState.offsetX,
                y: calibration.neutral.y + driftState.offsetY,
              },
            };
      const calibrated = applyCalibration(sample.gaze, effectiveCalibration, {
        gain: params.gain,
        invertX: params.invertX,
        invertY: params.invertY,
      });
      lastGazeHud = { raw: sample.gaze, calibrated, path: getLastGazePath() };
      const filtered = oneEuro.filter(calibrated, nowMs);
      lastFilteredGaze = filtered;
      const steerCfg = params.deadzone !== undefined ? { deadzone: params.deadzone } : {};
      let velocity = steerFromGaze(filtered, steerCfg);

      if (params.steer) {
        const open = openDirections(position, gameState.corridors, CURSOR_RADIUS);
        const judgmentForAssist = steerClient.getJudgment();
        const assistResult = blendAssist(velocity, judgmentForAssist, open, nowMs, {
          assistMax: params.steerAssist ?? ASSIST_DEFAULTS.assistMax,
        });
        velocity = assistResult.velocity;
        lastSteerHud = {
          intent: judgmentForAssist?.intent ?? null,
          confidence: judgmentForAssist?.confidence ?? 0,
          reliable: judgmentForAssist?.reliable ?? 0,
          assist: assistResult.assist,
          damp: assistResult.damp,
          reason: assistResult.reason,
          ageMs: judgmentForAssist !== null ? nowMs - judgmentForAssist.t : null,
        };
      } else {
        lastSteerHud = null;
      }

      position = applyVelocity(position, velocity, dtS, BOARD);
    }

    const cursor = position;
    const cursorForStep = uiOverlay === "none" ? cursor : null;
    const previousLevelIndex = gameState.levelIndex;
    const level = LEVELS[gameState.levelIndex] as Level;
    let frameTravelDx = 0;
    let frameTravelDy = 0;

    if (cursor !== null) {
      const dist = distanceToWall(cursor, gameState.corridors);
      frameTravelDx = cursor.x - prevPosition.x;
      frameTravelDy = cursor.y - prevPosition.y;
      const speedPxS = Math.hypot(frameTravelDx, frameTravelDy) / Math.max(dtS, 1 / 1000);
      const snapshot = telemetryWindow.snapshot({
        timeOnLevelS: (nowMs - gameState.levelStartedAt) / 1000,
        level: level.id,
        distToWallPx: dist,
        cursorSpeedPxS: speedPxS,
        now: nowMs,
      });
      focusClient.checkHealth();
      focusClient.poll(nowMs, snapshot);

      const pollMs = steerPollIntervalMs(
        speedPxS,
        dist,
        params.steerPoll ?? STEER_FAST_POLL_INTERVAL_MS_DEFAULT,
      );
      lastDriftHud = { offsetX: driftState.offsetX, offsetY: driftState.offsetY, pollMs };

      if (params.steer && mode === "EYES") {
        const openNow = openDirections(cursor, gameState.corridors, CURSOR_RADIUS);
        const jitter = Math.hypot(snapshot.gazeStdX, snapshot.gazeStdY);
        const blinkMean = sample !== null ? (sample.blinkL + sample.blinkR) / 2 : 0;
        const steerState: SteerState = {
          gazeMeanDx: clampRange(lastFilteredGaze?.x ?? 0, -1, 1),
          gazeMeanDy: clampRange(lastFilteredGaze?.y ?? 0, -1, 1),
          gazeJitter: clampRange(jitter, 0, 2),
          eyesOpen: sample !== null && sample.facePresent && blinkMean <= 0.5,
          blinkingNow: sample !== null && sample.facePresent && blinkMean > 0.5,
          headMotionDegPerSecond: clampRange(snapshot.headMotionDegPerS, 0, 1e4),
          openLeft: openNow.left,
          openRight: openNow.right,
          openUp: openNow.up,
          openDown: openNow.down,
          directionToGoal: directionToGoal(cursor, level.goal),
          distanceToNearestWallPx: clampRange(dist, -1e5, 1e5),
          cursorSpeedPxPerSecond: clampRange(speedPxS, 0, 1e5),
          level: level.id,
          centrelineOffsetPx: clampRange(
            signedCentrelineOffset(cursor, gameState.corridors),
            -1e4,
            1e4,
          ),
          recentWallSide: lastWallSide,
          windowMs: clampRange(snapshot.windowMs, 250, 60000),
        };
        steerClient.poll(nowMs, steerState);
      }

      prevPosition = cursor;
      lastTelemetry = {
        saccades: snapshot.saccades,
        blinksPerMin: snapshot.blinksPerMin,
        headMotionDegPerS: snapshot.headMotionDegPerS,
        facePresentRatio: snapshot.facePresentRatio,
      };
    }

    const judgment = focusClient.getJudgment();
    const focusThreshold = params.scareThreshold ?? FOCUS_THRESHOLD_DEFAULT;
    const { state: nextState, events } = stepGame(gameState, {
      cursor: cursorForStep,
      dtS,
      judgment,
      now: nowMs,
      focusThreshold,
    });
    gameState = nextState;
    if (events.includes("wall")) {
      lastWallSide = wallSideFromTravel(frameTravelDx, frameTravelDy);
    }
    handleEvents(events, previousLevelIndex);

    const banner =
      eyePipelineBanner ?? opts.cameraBanner ?? calibrationWeakBanner ?? focusClient.getBanner();
    setLiveBanner(opts.liveRegion, banner);

    const hud: HudInfo = {
      mode,
      focusP: judgment?.p ?? null,
      focusThreshold,
      focusStatus: computeFocusStatus(judgment, nowMs, focusThreshold),
      gaze: mode === "EYES" ? lastGazeHud : null,
      telemetry: lastTelemetry,
      fps,
      levelName: level.name,
      banner,
      steer: lastSteerHud,
      drift: lastDriftHud,
      scareArmed: scareScene.armedMode,
    };
    drawFrame(ctx, BOARD.w, BOARD.h, level, gameState.corridors, cursor, hud, opts.artBaseUrl);
  }

  rafId = requestAnimationFrame(frame);

  let stopped = false;
  function stop(): void {
    if (stopped) return;
    stopped = true;
    if (rafId !== null) cancelAnimationFrame(rafId);
    if (flashTimer !== null) clearTimeout(flashTimer);
    if (cardTimer !== null) clearTimeout(cardTimer);
    window.removeEventListener("keydown", handleKeydown);
    removeMouseListeners();
    stopMediaStreamTracks();
    landmarker?.close();
    calibrationOverlay.dispose();
  }

  return { stop };
}
